import { defineStore } from 'pinia';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import {
  clearSession,
  ensureFreshAccessToken,
  persistSessionTokens,
  refreshAccessToken
} from '@/utils/sessionExpiryHandler';
import { clearMobileUserCaches } from '@/pwa/registerMobileServiceWorker';

// 页面加载时从 localStorage 恢复 token，确保后续 axios 全局实例请求携带鉴权头
const _savedToken = localStorage.getItem('token');
if (_savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_savedToken}`;
}

const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin']);

function isAdminUser(user) {
  const role = String(user?.role || '').trim().toLowerCase();
  return ADMIN_ROLES.has(role);
}

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('token') || '',
    user: JSON.parse(localStorage.getItem('user')) || null,
    loading: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isAdmin: (state) => isAdminUser(state.user)
  },

  actions: {
    async login(username, password) {
      this.loading = true;
      try {
        // Use relative path, relying on proxy or base URL
        const response = await axios.post('/api/auth/login', { username, password });
        
        if (response.data.success) {
          const { token, refreshToken, user } = response.data.data;

          this.token = token;
          this.user = user;

          // Persist to local storage
          persistSessionTokens({ token, refreshToken });
          localStorage.setItem('user', JSON.stringify(user));
          
          ElMessage.success('登录成功');
          return true;
        }
      } catch (error) {
        const data = error.response?.data;
        const status = error.response?.status;
        let msg;
        if (status === 423) {
          // 账号锁定：优先用后端返回的剩余分钟数
          const minutes = data?.lockMinutes;
          msg = minutes && minutes > 0
            ? `账号已锁定，请 ${minutes} 分钟后再试`
            : (data?.message || '账号已锁定，请稍后再试');
        } else if (status === 401) {
          // 密码错误：展示剩余尝试次数，提示即将锁定
          const remaining = Number(data?.remaining);
          if (Number.isFinite(remaining) && remaining > 0) {
            msg = `用户名或密码错误，还可尝试 ${remaining} 次`;
          } else if (Number.isFinite(remaining) && remaining <= 0) {
            msg = '用户名或密码错误，账号将被锁定 15 分钟';
          } else {
            msg = data?.message || '用户名或密码错误';
          }
        } else {
          msg = data?.message || '登录失败，请检查网络或联系管理员';
        }
        // 401/423 是预期的业务失败（密码错 / 已锁定），不写 console.error；
        // 其余（网络错、5xx、未知状态码）才记录，便于排障。
        if (status !== 401 && status !== 423) {
          console.error('Login error:', error);
        }
        ElMessage.error(msg);
        return false;
      } finally {
        this.loading = false;
      }
    },

    logout(options = {}) {
      this.token = '';
      this.user = null;
      clearSession();
      void clearMobileUserCaches().catch(() => {});
      
      if (options.redirect !== false) {
        const isMobile = window.location.pathname === '/mobile' || window.location.pathname.startsWith('/mobile/');
        window.location.href = isMobile ? '/mobile/login' : '/login';
      }
    },

    // Check if token is valid on app start
    async checkAuth(options = {}) {
      const activeToken = await ensureFreshAccessToken();
      if (!activeToken) return false;

      this.token = activeToken;
      const authCheckConfig = {
        // 路由守卫负责区分首次导航和使用中失效，避免响应拦截器提前弹窗。
        _skipSessionExpiryPrompt: options.redirectOnFail === false
      };
      
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${activeToken}`;
        const response = await axios.get('/api/auth/me', authCheckConfig);
        if (response.data.success) {
          this.token = localStorage.getItem('token') || this.token;
          this.user = response.data.data.user; // Update user info
          localStorage.setItem('user', JSON.stringify(this.user));
          return true;
        }
        return false;
      } catch (error) {
        if (error.response?.status === 401) {
          const refreshed = await refreshAccessToken();
          if (refreshed?.token) {
            this.token = refreshed.token;
            axios.defaults.headers.common['Authorization'] = `Bearer ${refreshed.token}`;
            const response = await axios.get('/api/auth/me', authCheckConfig);
            if (response.data.success) {
              this.user = response.data.data.user;
              localStorage.setItem('user', JSON.stringify(this.user));
              return true;
            }
          }
        }

        if (options.redirectOnFail !== false) {
          this.logout();
        } else {
          this.token = '';
          this.user = null;
          clearSession();
        }
        return false;
      }
    }
  }
});
